import { useState, useCallback } from 'react';
import { useCreateCsvMigrationTemplateMutation } from '../api';
import { useNotificationContext } from '../providers/NotificationProvider';

/**
 * Custom hook for CSV download functionality
 * Replicates the Backbone triggerDownloadCSV logic
 */
export const useCsvDownload = () => {
    const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
    const { notifySuccess, notifyError } = useNotificationContext();

    const [createCsvTemplate] = useCreateCsvMigrationTemplateMutation();

    /**
     * Download CSV file by creating a temporary link and clicking it
     * Replicates the RM.downloadCSV function from helpers.js
     */
    const downloadCsvFile = useCallback(
        (url, filename) => {
            const apiRoot = '/api/v3/';
            const fullUrl = `${apiRoot}${url}`;

            fetch(fullUrl, {
                method: 'GET',
                headers: {
                    accept: 'text/csv',
                    'X-Auth-Token': sessionStorage.getItem('sessionToken'),
                },
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error('CSV file download failed');
                    }
                    return response.text();
                })
                .then((data) => {
                    // Create blob with BOM for proper CSV encoding
                    const blob = new Blob(['\ufeff', data], { type: 'text/csv;charset=utf-8' });
                    const url = URL.createObjectURL(blob);

                    // Create temporary download link
                    const downloadLink = document.createElement('a');
                    downloadLink.href = url;
                    downloadLink.download = filename;
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);

                    // Clean up the URL object
                    URL.revokeObjectURL(url);
                })
                .catch((error) => {
                    console.error('CSV download error:', error);
                    notifyError({ description: 'CSV file download failed.' });
                });
        },
        [notifyError],
    );

    /**
     * Trigger CSV download with polling
     * Replicates the RM.triggerDownloadCSV function from helpers.js
     */
    const triggerCsvDownload = useCallback(
        async (downloadPayload, successCallback) => {
            try {
                setIsDownloadingCsv(true);

                // Create CSV migration template
                const result = await createCsvTemplate(downloadPayload).unwrap();
                const csvId = result.id;

                // Show initial success notification
                notifySuccess({
                    description:
                        'Migrations CSV file generation in progress. Download will start automatically once it completes.',
                });

                // Start polling for completion
                const pollInterval = setInterval(async () => {
                    try {
                        const response = await fetch(`/api/v3/csvmigrations/template/${csvId}`, {
                            method: 'GET',
                            headers: {
                                'X-Auth-Token': sessionStorage.getItem('sessionToken'),
                            },
                        });

                        if (!response.ok) {
                            throw new Error('Failed to check CSV generation status');
                        }

                        const data = await response.json();

                        if (data.state === 'success') {
                            // CSV is ready, trigger download
                            const downloadUrl = `csvmigrations/template/${csvId}?format=csv&token=${sessionStorage.getItem('sessionToken')}`;
                            downloadCsvFile(downloadUrl, 'migrate.csv');

                            // Call success callback if provided
                            if (successCallback) {
                                successCallback();
                            }

                            clearInterval(pollInterval);
                            setIsDownloadingCsv(false);
                        } else if (data.state === 'error') {
                            throw new Error('CSV generation failed');
                        }
                        // Continue polling if state is still 'processing'
                    } catch (error) {
                        console.error('CSV polling error:', error);
                        notifyError({ description: 'Failed to download Migrations CSV file.' });
                        clearInterval(pollInterval);
                        setIsDownloadingCsv(false);
                    }
                }, 1000); // Poll every second
            } catch (error) {
                console.error('CSV download initiation error:', error);
                notifyError({ description: 'Migrations CSV file generation failed.' });
                setIsDownloadingCsv(false);
            }
        },
        [createCsvTemplate, downloadCsvFile, notifySuccess, notifyError],
    );

    return {
        isDownloadingCsv,
        triggerCsvDownload,
    };
};
